import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { BlueBorderButton, BlueButton } from 'components/Button';
import Modal, { ModalHeader, ModalMessage } from 'components/Modal';
import Break from 'components/Break';
import Text from 'components/Text';
import SelectableSearchInput from 'components/SelectableSearchInput';
import {
  ERROR_MESSAGES,
  SPECIAL_CHARACTERS_ALLOWED,
  USER_FRIENDLY_AUTH_ROLES,
  USER_FRIENDLY_TO_DATABASE_AUTH_ROLES,
  USER_NAME_MAX_LENGTH
} from 'utils/appConstants';
import {
  isAssignedRoleValid,
  isLengthValid,
  isNoSpecialCharactersValid
} from 'utils/validate';

const authRoles = Object.keys(USER_FRIENDLY_AUTH_ROLES).map(
  key => USER_FRIENDLY_AUTH_ROLES[key]
);

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  height: 33px;
  margin: ${({ margin }) => (margin ? margin : '1em 0')};
`;

const InputLabel = styled(ModalMessage)`
  flex: 1 1 40%;
  font-size: 12px;
  line-height: 16px;
`;

const Input = styled.input`
  color: #4a4a4a;
  flex: 1 1 60%;
  font-family: Crossten;
  font-size: 16px;
  box-sizing: border-box;
  border-radius: 2px;
  border: 1px solid #d8d8d8;
  outline: none;
  padding: 1em 0.5em;
  border: ${({ isError }) =>
    isError ? '1px solid #EA6E6D' : '1px solid #d8d8d8'};
`;

const Info = styled(ModalMessage)`
  flex: 1 1 60%;
  box-sizing: border-box;
  line-height: 33px;
  padding: 0;
`;

const DropdownInput = styled(SelectableSearchInput)`
  flex: 1 1 60%;

  [class*='-Control'] {
    height: 100%;
  }

  [class*='-singleValue'] {
    color: #4a4a4a;
    font-size: 16px;
  }
`;

const RemoveUserButtonContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 60%;
`;

const ButtonRow = styled.div``;

const InputErrorText = styled(Text)`
  color: #ea6e6d;
  font-size: 14px;
  margin: 12px;
`;

const EditUserModal = ({ onSubmit, onClose, user, users, handleDelete }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [inputErrors, setInputErrors] = useState({
    firstName: [],
    lastName: [],
    role: []
  });

  useEffect(() => {
    const { firstName, lastName, email, roleName } = user;
    setFirstName(firstName);
    setLastName(lastName);
    setEmail(email);
    setRole(roleName);
  }, []);

  const handleInputChange = (event, validationObject) => {
    const target = event.target;
    const value = target.value;
    const name = target.name;

    const { SPECIAL_CHARACTERS_ERROR, MAX_LENGTH_ERROR } = ERROR_MESSAGES;
    let errorMessages = [];
    if (validationObject) {
      if (
        validationObject.pattern &&
        !isNoSpecialCharactersValid(value, validationObject.pattern)
      ) {
        errorMessages.push(SPECIAL_CHARACTERS_ERROR);
      }
      if (
        validationObject.maxLength &&
        !isLengthValid(value, validationObject.maxLength)
      ) {
        errorMessages.push(MAX_LENGTH_ERROR(validationObject.maxLength));
      }

      if (errorMessages.length) {
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          [name]: errorMessages
        }));
      } else {
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          [name]: []
        }));
      }
    }
    if (name === 'firstName') {
      setFirstName(value);
    } else if (name === 'lastName') {
      setLastName(value);
    }
  };

  const handleSubmit = event => {
    event.preventDefault();
    const databaseRole =
      USER_FRIENDLY_TO_DATABASE_AUTH_ROLES[role.toUpperCase()];

    if (validatePayload()) {
      onSubmit({ id: user.id, firstName, lastName, role: databaseRole });
    }
  };

  const validatePayload = () => {
    const {
      SPECIAL_CHARACTERS_ERROR,
      MAX_LENGTH_ERROR,
      INVALID_PERMISSIONS_ASSIGNMENT,
      PERMISSION_SETTINGS_REQUIRED
    } = ERROR_MESSAGES;
    const databaseRole =
      USER_FRIENDLY_TO_DATABASE_AUTH_ROLES[role.toUpperCase()];

    let isValid = true;
    let errorMessages = [];

    const verifyName = verifyFirstLast => {
      if (!isLengthValid(verifyFirstLast, USER_NAME_MAX_LENGTH)) {
        errorMessages.push(MAX_LENGTH_ERROR(USER_NAME_MAX_LENGTH));
      }
      if (!isNoSpecialCharactersValid(verifyFirstLast)) {
        errorMessages.push(SPECIAL_CHARACTERS_ERROR);
      }
      if (errorMessages.length) {
        isValid = false;
        setInputErrors(prevInputErrors => ({
          ...prevInputErrors,
          verifyFirstLast: errorMessages
        }));
        errorMessages = [];
      }
    };
    verifyName(firstName);
    verifyName(lastName);

    //role
    if (!databaseRole) {
      errorMessages.push(PERMISSION_SETTINGS_REQUIRED);
    } else if (!isAssignedRoleValid(databaseRole)) {
      errorMessages.push(INVALID_PERMISSIONS_ASSIGNMENT);
    }
    if (errorMessages.length) {
      isValid = false;
      setInputErrors(prevInputErrors => ({
        ...prevInputErrors,
        role: errorMessages
      }));
      errorMessages = [];
    }

    if (isValid) {
      return true;
    }
  };

  const isLastAdmin = () => {
    const usersWithAdminAccess = users.filter(
      user => user.roleName === 'AdminCreateUser' || user.roleName === 'Admin'
    );
    if (
      usersWithAdminAccess.length <= 1 &&
      usersWithAdminAccess[0].id === user.id
    ) {
      return true;
    }
  };

  return (
    <Modal
      as="form"
      style={{
        padding: '2em 3em',
        minWidth: '575px',
        minHeight: '250px',
        boxSizing: 'border-box'
      }}
      onSubmit={handleSubmit}
    >
      <ModalHeader>Edit User Info</ModalHeader>

      <InfoRow>
        <InputLabel>First Name</InputLabel>
        <Input
          required
          type="text"
          name="firstName"
          onChange={e =>
            handleInputChange(e, {
              maxLength: USER_NAME_MAX_LENGTH,
              pattern: SPECIAL_CHARACTERS_ALLOWED
            })
          }
          isError={inputErrors.firstName.length}
          value={firstName}
          maxLength={USER_NAME_MAX_LENGTH}
          pattern={SPECIAL_CHARACTERS_ALLOWED}
        />
      </InfoRow>
      {!!inputErrors.firstName.length &&
        inputErrors.firstName.map((error, i) => (
          <InputErrorText key={i}>{error}</InputErrorText>
        ))}

      <InfoRow>
        <InputLabel>Last Name</InputLabel>
        <Input
          required
          type="text"
          name="lastName"
          onChange={e =>
            handleInputChange(e, {
              maxLength: USER_NAME_MAX_LENGTH,
              pattern: SPECIAL_CHARACTERS_ALLOWED
            })
          }
          isError={inputErrors.lastName.length}
          value={lastName}
          maxLength={USER_NAME_MAX_LENGTH}
          pattern={SPECIAL_CHARACTERS_ALLOWED}
        />
      </InfoRow>
      {!!inputErrors.lastName.length &&
        inputErrors.lastName.map((error, i) => (
          <InputErrorText key={i}>{error}</InputErrorText>
        ))}

      <InfoRow margin="0">
        <InputLabel>Email Address</InputLabel>
        <Info>{email}</Info>
      </InfoRow>

      <InfoRow margin="0.5em 0">
        <InputLabel>Permissions</InputLabel>
        {isLastAdmin() ? (
          <Info>Admin</Info>
        ) : (
          <DropdownInput
            name="role"
            isMulti={false}
            options={authRoles}
            onChange={selected => {
              const role = selected && selected.value ? selected.value : '';
              setRole(role);
              setInputErrors(prevInputErrors => ({
                ...prevInputErrors,
                role: []
              }));
            }}
            isError={inputErrors.role.length}
            selected={role}
          />
        )}
      </InfoRow>
      {!!inputErrors.role.length &&
        inputErrors.role.map((error, i) => (
          <Text color="#EA6E6D" fontSize="14px" margin="12px" key={i}>
            {error}
          </Text>
        ))}

      <InfoRow>
        <InputLabel>Remove User Access</InputLabel>
        <RemoveUserButtonContainer>
          <BlueBorderButton
            type="button"
            onClick={handleDelete}
            disabled={isLastAdmin()}
          >
            Remove User
          </BlueBorderButton>
        </RemoveUserButtonContainer>
      </InfoRow>

      <Break margin="2em 0" />

      <ButtonRow>
        <BlueBorderButton margin="0 16px 0 0" type="button" onClick={onClose}>
          Close
        </BlueBorderButton>
        <BlueButton type="submit">Save Changes</BlueButton>
      </ButtonRow>
    </Modal>
  );
};

export default EditUserModal;

EditUserModal.propTypes = {
  user: PropTypes.object.isRequired,
  users: PropTypes.array.isRequired,
  onSubmit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};
